import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
    Download, 
    Upload, 
    FileText, 
    Lock, 
    Eye,
    EyeOff,
    Loader2,
    AlertTriangle,
    Check,
    ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import { encryptData, decryptData, generateRandomPassword } from '../lib/encryption';
import { jsPDF } from 'jspdf';

export const ExportDialog = ({
    open,
    onClose,
    ideas,
    onImport,
}) => {
    const [activeTab, setActiveTab] = useState('export');
    const [exportType, setExportType] = useState('encrypted');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPassword, setImportPassword] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [importError, setImportError] = useState('');
    
    const handleGeneratePassword = () => {
        const newPassword = generateRandomPassword();
        setPassword(newPassword);
        setShowPassword(true);
    };

    const generatePDF = (ideasToExport) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        const maxWidth = pageWidth - (margin * 2);
        let yPosition = 20;
        
        // Title
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('IdeaVault Export', margin, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(128);
        doc.text(`Exported on ${new Date().toLocaleDateString()}`, margin, yPosition);
        doc.setTextColor(0);
        yPosition += 15;
        
        ideasToExport.forEach((idea, index) => {
            // Check if we need a new page
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
            }
            
            // Idea title
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            const title = idea.title || 'Untitled';
            doc.text(title, margin, yPosition);
            yPosition += 8;
            
            // Date
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(128);
            doc.text(`Updated: ${new Date(idea.updatedAt).toLocaleDateString()}`, margin, yPosition);
            doc.setTextColor(0);
            yPosition += 8;
            
            // Content
            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            const content = idea.content || '(No content)';
            const lines = doc.splitTextToSize(content, maxWidth);
            
            lines.forEach(line => {
                if (yPosition > 270) {
                    doc.addPage();
                    yPosition = 20;
                }
                doc.text(line, margin, yPosition);
                yPosition += 6;
            });
            
            yPosition += 10;
            
            // Separator
            if (index < ideasToExport.length - 1) {
                doc.setDrawColor(200);
                doc.line(margin, yPosition, pageWidth - margin, yPosition);
                yPosition += 10;
            }
        });
        
        return doc;
    };
    
    const handleExport = async () => {
        if (ideas.length === 0) {
            toast.error('No ideas to export');
            return;
        }
        
        setIsProcessing(true);
        
        try {
            if (exportType === 'pdf') {
                // Generate PDF
                const doc = generatePDF(ideas);
                doc.save(`ideavault-ideas-${new Date().toISOString().split('T')[0]}.pdf`);
                toast.success(`Exported ${ideas.length} idea${ideas.length !== 1 ? 's' : ''} as PDF`);
                
            } else if (exportType === 'encrypted') {
                // Encrypted backup
                if (!password) {
                    toast.error('Please enter or generate a password');
                    setIsProcessing(false);
                    return;
                }
                
                const exportData = {
                    version: 1,
                    exportedAt: new Date().toISOString(),
                    ideas,
                };
                
                const encrypted = await encryptData(exportData, password);
                const data = JSON.stringify({ encrypted, version: 1 });
                const blob = new Blob([data], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `ideavault-backup-${new Date().toISOString().split('T')[0]}.ivbackup`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                
                toast.success(`Exported ${ideas.length} idea${ideas.length !== 1 ? 's' : ''}`);
                toast.info('Keep your password safe. You need it to restore.', { duration: 5000 });
            }
            
        } catch (error) {
            toast.error('Export failed: ' + error.message);
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImportFile(file);
            setImportError('');
        }
    };
    
    const handleImport = async () => {
        if (!importFile) {
            toast.error('Please select a file');
            return;
        }
        
        setIsProcessing(true);
        setImportError('');
        
        try {
            const text = await importFile.text();
            let importedIdeas;
            
            const parsed = JSON.parse(text);
            
            if (parsed.encrypted) {
                if (!importPassword) {
                    setImportError('Please enter the password');
                    setIsProcessing(false);
                    return;
                }
                
                const decrypted = await decryptData(parsed.encrypted, importPassword);
                importedIdeas = decrypted.ideas;
                
            } else if (Array.isArray(parsed)) {
                importedIdeas = parsed;
            } else if (parsed.ideas && Array.isArray(parsed.ideas)) {
                importedIdeas = parsed.ideas;
            } else {
                throw new Error('Invalid file format');
            }
            
            if (!importedIdeas.every(idea => idea.id && idea.createdAt)) {
                throw new Error('Invalid idea format');
            }
            
            await onImport(importedIdeas);
            toast.success(`Imported ${importedIdeas.length} idea${importedIdeas.length !== 1 ? 's' : ''}`);
            onClose();
            
        } catch (error) {
            if (error.message.includes('Decryption failed')) {
                setImportError('Wrong password. Please try again.');
            } else {
                setImportError('Import failed: ' + error.message);
            }
        } finally {
            setIsProcessing(false);
        }
    };
    
    const resetState = () => {
        setPassword('');
        setImportFile(null);
        setImportPassword('');
        setImportError('');
        setShowPassword(false);
        setExportType('encrypted');
    };
    
    return (
        <Dialog open={open} onOpenChange={(isOpen) => {
            if (!isOpen) resetState();
            onClose();
        }}>
            <DialogContent 
                className="sm:max-w-md rounded-2xl border-border max-h-[90vh] overflow-y-auto"
                data-testid="export-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-2xl font-light text-foreground">
                        Export & Import
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        Backup your ideas or restore from a previous backup.
                    </DialogDescription>
                </DialogHeader>
                
                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                    <TabsList className="grid grid-cols-2 h-11 p-1 bg-accent rounded-full">
                        <TabsTrigger 
                            value="export" 
                            className="rounded-full text-sm h-9 data-[state=active]:bg-card"
                            data-testid="export-tab"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Export
                        </TabsTrigger>
                        <TabsTrigger 
                            value="import" 
                            className="rounded-full text-sm h-9 data-[state=active]:bg-card"
                            data-testid="import-tab"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            Import
                        </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="export" className="mt-6 space-y-4">
                        {/* Export Type Selection */}
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => setExportType('encrypted')}
                                className={`p-4 rounded-xl border text-left transition-all duration-150 ${
                                    exportType === 'encrypted' 
                                        ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                                        : 'border-border hover:border-primary/50'
                                }`}
                                data-testid="export-type-encrypted"
                            >
                                <Lock className="h-5 w-5 mb-2 text-foreground" />
                                <div className="font-medium text-sm text-foreground">Encrypted</div>
                                <div className="text-xs text-muted-foreground mt-0.5">Full backup, secure</div>
                            </button>
                            <button
                                onClick={() => setExportType('pdf')}
                                className={`p-4 rounded-xl border text-left transition-all duration-150 ${
                                    exportType === 'pdf' 
                                        ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                                        : 'border-border hover:border-primary/50'
                                }`}
                                data-testid="export-type-pdf"
                            >
                                <FileText className="h-5 w-5 mb-2 text-foreground" />
                                <div className="font-medium text-sm text-foreground">PDF</div>
                                <div className="text-xs text-muted-foreground mt-0.5">Readable document</div>
                            </button>
                        </div>
                        
                        {/* PDF Warning */}
                        {exportType === 'pdf' && (
                            <div className="p-3 bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/20 rounded-xl flex gap-3">
                                <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                                    PDF export is not encrypted. Anyone with the file can read your ideas. 
                                    Use Encrypted export for backups you want to protect.
                                </p>
                            </div>
                        )}
                        
                        {/* Password for encrypted export */}
                        {exportType === 'encrypted' && (
                            <div className="space-y-3">
                                <Label htmlFor="export-password" className="text-foreground">Encryption Password</Label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Input
                                            id="export-password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter a strong password"
                                            className="pr-10 rounded-xl h-11"
                                            data-testid="export-password-input"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-muted-foreground" />
                                            )}
                                        </Button>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        onClick={handleGeneratePassword}
                                        className="rounded-xl h-11 whitespace-nowrap"
                                        data-testid="generate-password-btn"
                                    >
                                        Generate
                                    </Button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Save this password. Without it, you cannot restore your backup.
                                </p>
                            </div>
                        )}
                        
                        <Button
                            onClick={handleExport}
                            disabled={isProcessing || (exportType === 'encrypted' && !password)}
                            className="w-full rounded-full h-11 bg-primary hover:bg-primary/90"
                            data-testid="export-btn-dialog"
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4 mr-2" />
                                    Export {ideas.length} Idea{ideas.length !== 1 ? 's' : ''}
                                </>
                            )}
                        </Button>
                        
                        <p className="text-xs text-muted-foreground text-center">
                            Export is processed entirely in your browser. Nothing is uploaded.
                        </p>
                    </TabsContent>
                    
                    <TabsContent value="import" className="mt-6 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="import-file" className="text-foreground">Select Backup File</Label>
                            <Input
                                id="import-file"
                                type="file"
                                accept=".ivbackup,.qrbackup,.json"
                                onChange={handleFileSelect}
                                className="rounded-xl h-11 cursor-pointer file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-accent file:text-foreground hover:file:bg-accent/80"
                                data-testid="import-file-input"
                            />
                            {importFile && (
                                <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                                    <Check className="h-4 w-4" />
                                    {importFile.name}
                                </div>
                            )}
                        </div>
                        
                        {importFile?.name.match(/\.(ivbackup|qrbackup)$/) && (
                            <div className="space-y-2">
                                <Label htmlFor="import-password" className="text-foreground">Password</Label>
                                <Input
                                    id="import-password"
                                    type="password"
                                    value={importPassword}
                                    onChange={(e) => setImportPassword(e.target.value)}
                                    placeholder="Enter the backup password"
                                    className="rounded-xl h-11"
                                    data-testid="import-password-input"
                                />
                            </div>
                        )}
                        
                        {importError && (
                            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
                                <p className="text-sm text-destructive">{importError}</p>
                            </div>
                        )}
                        
                        <div className="p-3 bg-accent border border-border rounded-xl flex gap-3">
                            <AlertTriangle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                Importing will add ideas to your existing collection. 
                                Ideas with duplicate IDs will be overwritten.
                            </p>
                        </div>
                        
                        <Button
                            onClick={handleImport}
                            disabled={isProcessing || !importFile}
                            className="w-full rounded-full h-11 bg-primary hover:bg-primary/90"
                            data-testid="import-btn-dialog"
                        >
                            {isProcessing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Import Backup
                                </>
                            )}
                        </Button>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};
